import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react'
import {
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
} from 'firebase/auth'
import { initializeApp, getApps } from 'firebase/app'

type UserAuthContextType = {
  user: ReturnType<typeof getAuth>['currentUser'] | null
  isAuthenticated: boolean
  loading: boolean
  signInWithGoogle: () => Promise<void>
  signInWithApple: () => Promise<void>
  signOutUser: () => Promise<void>
  getIdToken: () => Promise<string | null>
}

const UserAuthContext = createContext<UserAuthContextType | null>(null)

// Firebase app singleton
function getFirebaseAuth() {
  if (!getApps().length) {
    initializeApp({
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    })
  }
  return getAuth()
}

export function UserAuthProvider({ children }: { children: ReactNode }) {
  const auth = useMemo(() => getFirebaseAuth(), [])
  const [user, setUser] = useState<ReturnType<typeof getAuth>['currentUser'] | null>(auth.currentUser)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (next) => {
      setUser(next)
      setLoading(false)
    })
    return unsub
  }, [auth])

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider()
    await signInWithPopup(auth, provider)
  }

  const signInWithApple = async () => {
    const provider = new OAuthProvider('apple.com')
    provider.addScope('email')
    provider.addScope('name')
    await signInWithPopup(auth, provider)
  }

  const signOutUser = async () => {
    await signOut(auth)
  }

  const getIdToken = async () => {
    const current = auth.currentUser
    if (!current) return null
    return await current.getIdToken(true)
  }

  return (
    <UserAuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        signInWithGoogle,
        signInWithApple,
        signOutUser,
        getIdToken,
      }}
    >
      {children}
    </UserAuthContext.Provider>
  )
}

export function useUserAuth() {
  const ctx = useContext(UserAuthContext)
  if (!ctx) {
    throw new Error('useUserAuth must be used within UserAuthProvider')
  }
  return ctx
}

// Lightweight helper for interceptors
export async function getUserIdToken() {
  const auth = getFirebaseAuth()
  const current = auth.currentUser
  if (!current) return null
  return await current.getIdToken(true)
}
